import {
  AutoCompleteMultiUncontrolled,
  Button,
  DatePickerUncontrolled,
  DateRangeSelect,
  ISelectType,
} from "@4uhub/lib4uhub";
import {
  Box,
  FormHelperText,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ReportMonthPeriodFilter from "./ReportMonthPeriodFilter";
import type { TReportMonthPeriodType } from "./reportExecutionUtils";

export interface IReportExecutionValidationErrors {
  createdStart?: string;
  createdEnd?: string;
  createdRange?: string;
  created?: string;
  ticketServices?: string;
  workspaces?: string;
  ticketChannels?: string;
}

interface IReportExecutionFiltersProps {
  title: string;
  reportCode?: string | number | null;
  createdStart: Date | null;
  createdEnd: Date | null;
  selectedDateInterval: ISelectType | null;
  selectedMonthPeriodType: TReportMonthPeriodType;
  selectedTicketServices: ISelectType[];
  selectedTicketChannels: ISelectType[];
  selectedWorkspaces: ISelectType[];
  selectedUsers: ISelectType[];
  selectedAutomationCode: ISelectType[];
  selectedAutomationCodeExcept: ISelectType[];
  selectedEstablishments: ISelectType[];
  selectedEsignRules: ISelectType[];
  ticketServiceOptions: ISelectType[];
  ticketChannelOptions: ISelectType[];
  workspaceOptions: ISelectType[];
  userOptions: ISelectType[];
  automationOptions: ISelectType[];
  establishmentOptions: ISelectType[];
  esignRuleOptions: ISelectType[];
  shouldShowTicketServices: boolean;
  shouldRequireTicketServices: boolean;
  shouldShowTicketChannels: boolean;
  shouldRequireTicketChannels: boolean;
  shouldShowWorkspaces: boolean;
  shouldShowUsers: boolean;
  shouldUseMonthPeriod: boolean;
  shouldShowAutomationCode: boolean;
  shouldShowAutomationCodeExcept: boolean;
  shouldShowEstablishments: boolean;
  shouldShowEsignRules: boolean;
  isLoading: boolean;
  isGenerateDisabled: boolean;
  validationErrors: IReportExecutionValidationErrors;
  onCreatedStartChange: (date: Date | null) => void;
  onCreatedEndChange: (date: Date | null) => void;
  onDateIntervalChange: (value: ISelectType | null) => void;
  onMonthPeriodTypeChange: (value: TReportMonthPeriodType) => void;
  onSelectedMonthChange: (date: Date | null) => void;
  onTicketServicesChange: (value: ISelectType[] | null) => void;
  onTicketChannelsChange: (value: ISelectType[] | null) => void;
  onWorkspacesChange: (value: ISelectType[] | null) => void;
  onUsersChange: (value: ISelectType[] | null) => void;
  onAutomationCodeChange: (value: ISelectType[] | null) => void;
  onAutomationCodeExceptChange: (value: ISelectType[] | null) => void;
  onEstablishmentsChange: (value: ISelectType[] | null) => void;
  onEsignRulesChange: (value: ISelectType[] | null) => void;
  onGenerate: () => void;
}

const REPORT_EXECUTION_TRANSLATION_PATH = "page.reports.execution.";

const isValidDate = (date?: Date | null): date is Date =>
  date instanceof Date && !Number.isNaN(date.getTime());

const ReportExecutionFilters: React.FC<IReportExecutionFiltersProps> = ({
  title,
  reportCode,
  createdStart,
  createdEnd,
  selectedDateInterval,
  selectedMonthPeriodType,
  selectedTicketServices,
  selectedTicketChannels,
  selectedWorkspaces,
  selectedUsers,
  selectedAutomationCode,
  selectedAutomationCodeExcept,
  selectedEstablishments,
  selectedEsignRules,
  ticketServiceOptions,
  ticketChannelOptions,
  workspaceOptions,
  userOptions,
  automationOptions,
  establishmentOptions,
  esignRuleOptions,
  shouldShowTicketServices,
  shouldRequireTicketServices,
  shouldShowTicketChannels,
  shouldRequireTicketChannels,
  shouldShowWorkspaces,
  shouldShowUsers,
  shouldUseMonthPeriod,
  shouldShowAutomationCode,
  shouldShowAutomationCodeExcept,
  shouldShowEstablishments,
  shouldShowEsignRules,
  isLoading,
  isGenerateDisabled,
  validationErrors,
  onCreatedStartChange,
  onCreatedEndChange,
  onDateIntervalChange,
  onMonthPeriodTypeChange,
  onSelectedMonthChange,
  onTicketServicesChange,
  onTicketChannelsChange,
  onWorkspacesChange,
  onUsersChange,
  onAutomationCodeChange,
  onAutomationCodeExceptChange,
  onEstablishmentsChange,
  onEsignRulesChange,
  onGenerate,
}) => {
  const { t } = useTranslation();
  const [disableDateFields, setDisableDateFields] = useState(
    selectedDateInterval?.id !== "select_period",
  );

  useEffect(() => {
    setDisableDateFields(selectedDateInterval?.id !== "select_period");
  }, [selectedDateInterval]);

  const handleDateRangeStartChange = (date: Date | null) => {
    if (selectedDateInterval?.id === "select_period" && date === null) {
      return;
    }

    onCreatedStartChange(date);
  };

  const handleDateRangeEndChange = (date: Date | null) => {
    if (selectedDateInterval?.id === "select_period" && date === null) {
      return;
    }

    onCreatedEndChange(date);
  };

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        p: 3,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
      })}
    >
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {title}
          </Typography>
          {reportCode !== undefined && reportCode !== null ? (
            <Typography
              sx={{ mt: 0.5, color: "text.secondary", fontWeight: 600 }}
              variant="body2"
            >
              {t(REPORT_EXECUTION_TRANSLATION_PATH + "code")}: {reportCode}
            </Typography>
          ) : null}
        </Box>

        <Grid container spacing={2}>
          {shouldUseMonthPeriod ? (
            <ReportMonthPeriodFilter
              createdError={validationErrors.created}
              createdStart={createdStart}
              selectedPeriod={selectedMonthPeriodType}
              onPeriodChange={onMonthPeriodTypeChange}
              onSelectedMonthChange={onSelectedMonthChange}
            />
          ) : (
            <>
              <Grid item xs={12} md={4} lg={3}>
                <DateRangeSelect
                  setStartDate={handleDateRangeStartChange}
                  setEndDate={handleDateRangeEndChange}
                  setEnableFields={setDisableDateFields}
                  value={selectedDateInterval}
                  initialOption={selectedDateInterval}
                  onValueSelected={onDateIntervalChange}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <Stack spacing={0.5}>
                  <DatePickerUncontrolled
                    label={
                      t(
                        REPORT_EXECUTION_TRANSLATION_PATH + "startDate",
                        "Data Inicial",
                      ) + " *"
                    }
                    value={createdStart}
                    onChange={onCreatedStartChange}
                    fullwidth
                    isClearable={false}
                    disable={disableDateFields}
                  />
                  {(validationErrors.createdStart ||
                    validationErrors.createdRange) && (
                    <FormHelperText error>
                      {validationErrors.createdStart ??
                        validationErrors.createdRange}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <Stack spacing={0.5}>
                  <DatePickerUncontrolled
                    label={
                      t(
                        REPORT_EXECUTION_TRANSLATION_PATH + "endDate",
                        "Data Final",
                      ) + " *"
                    }
                    value={createdEnd}
                    onChange={onCreatedEndChange}
                    minDate={
                      isValidDate(createdStart) ? createdStart : undefined
                    }
                    fullwidth
                    isClearable={false}
                    disable={disableDateFields}
                  />
                  {(validationErrors.createdEnd ||
                    validationErrors.createdRange) && (
                    <FormHelperText error>
                      {validationErrors.createdEnd ??
                        validationErrors.createdRange}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>
            </>
          )}

          {shouldShowTicketServices && (
            <Grid item xs={12} lg={5}>
              <AutoCompleteMultiUncontrolled
                value={selectedTicketServices}
                label={
                  t(
                    REPORT_EXECUTION_TRANSLATION_PATH + "ticketServices",
                    "Serviços de Ticket",
                  ) as string
                }
                onValueSelected={onTicketServicesChange}
                options={ticketServiceOptions}
                size="small"
                multiline
                required={shouldRequireTicketServices}
                error={!!validationErrors.ticketServices}
                helperText={validationErrors.ticketServices}
              />
            </Grid>
          )}

          {shouldShowWorkspaces && (
            <Grid item xs={12} lg={5}>
              <Stack spacing={0.5}>
                <AutoCompleteMultiUncontrolled
                  value={selectedWorkspaces}
                  label={
                    t(
                      REPORT_EXECUTION_TRANSLATION_PATH + "workspaces",
                      "Áreas de Trabalho",
                    ) as string
                  }
                  onValueSelected={onWorkspacesChange}
                  options={workspaceOptions}
                  size="small"
                  multiline
                  required
                  error={!!validationErrors.workspaces}
                />
                {validationErrors.workspaces && (
                  <FormHelperText error>
                    {validationErrors.workspaces}
                  </FormHelperText>
                )}
              </Stack>
            </Grid>
          )}

          {shouldShowTicketChannels && (
            <Grid item xs={12} lg={5}>
              <AutoCompleteMultiUncontrolled
                value={selectedTicketChannels}
                label={
                  t(
                    REPORT_EXECUTION_TRANSLATION_PATH + "channels",
                    "Quadros",
                  ) as string
                }
                onValueSelected={onTicketChannelsChange}
                options={ticketChannelOptions}
                size="small"
                multiline
                required={shouldRequireTicketChannels}
                error={!!validationErrors.ticketChannels}
                helperText={validationErrors.ticketChannels}
              />
            </Grid>
          )}

          {shouldShowUsers && (
            <Grid item xs={12} lg={5}>
              <AutoCompleteMultiUncontrolled
                value={selectedUsers}
                label={
                  t(
                    REPORT_EXECUTION_TRANSLATION_PATH + "attendants",
                    "Usuários",
                  ) as string
                }
                onValueSelected={onUsersChange}
                options={userOptions}
                size="small"
                multiline
              />
            </Grid>
          )}

          {shouldShowAutomationCode && (
            <Grid item xs={12} lg={5}>
              <AutoCompleteMultiUncontrolled
                value={selectedAutomationCode}
                label={
                  t(
                    REPORT_EXECUTION_TRANSLATION_PATH + "automationCode",
                    "Automações",
                  ) as string
                }
                onValueSelected={onAutomationCodeChange}
                options={automationOptions}
                size="small"
                multiline
              />
            </Grid>
          )}

          {shouldShowAutomationCodeExcept && (
            <Grid item xs={12} lg={5}>
              <AutoCompleteMultiUncontrolled
                value={selectedAutomationCodeExcept}
                label={
                  t(
                    REPORT_EXECUTION_TRANSLATION_PATH + "automationCodeExcept",
                    "Exceto Automações",
                  ) as string
                }
                onValueSelected={onAutomationCodeExceptChange}
                options={automationOptions}
                size="small"
                multiline
              />
            </Grid>
          )}

          {shouldShowEstablishments && (
            <Grid item xs={12} lg={5}>
              <AutoCompleteMultiUncontrolled
                value={selectedEstablishments}
                label={
                  t(
                    REPORT_EXECUTION_TRANSLATION_PATH + "establishments",
                    "Estabelecimentos",
                  ) as string
                }
                onValueSelected={onEstablishmentsChange}
                options={establishmentOptions}
                size="small"
                multiline
              />
            </Grid>
          )}

          {shouldShowEsignRules && (
            <Grid item xs={12} lg={5}>
              <AutoCompleteMultiUncontrolled
                value={selectedEsignRules}
                label={
                  t(
                    REPORT_EXECUTION_TRANSLATION_PATH + "esignRules",
                    "Regras de Assinatura",
                  ) as string
                }
                onValueSelected={onEsignRulesChange}
                options={esignRuleOptions}
                size="small"
                multiline
              />
            </Grid>
          )}
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            onClick={onGenerate}
            loading={isLoading}
            disabled={isGenerateDisabled}
          >
            {
              t(
                REPORT_EXECUTION_TRANSLATION_PATH + "generate",
                "Gerar Relatório",
              ) as string
            }
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};

export default ReportExecutionFilters;
